/**
 * Imprime um documento HTML completo sem abrir janela nova.
 *
 * Existe por causa do UX-027. O recibo do financeiro imprimia assim:
 *
 * ```
 * const printWindow = window.open('', '_blank', 'width=400,height=600');
 * if (!printWindow) return;
 * ```
 *
 * Com o popup bloqueado — o padrão em celular e em muita instalação de desktop —
 * o botão não fazia **nada** e ninguém era avisado. Foi exatamente isso que
 * impediu o cambone financeiro de ver o recibo nas duas tentativas dele.
 *
 * **O que o retorno significa, exatamente.** `false` diz que não deu nem para
 * montar o documento — nesse caso nada foi tentado. `true` diz que o documento
 * foi montado e a impressão foi PEDIDA; não diz que o papel saiu. Chamar
 * `print()` não é prova de impressão física, e há navegador que ignora o pedido
 * **sem lançar erro** (relatado em Safari, quando o gesto do usuário já
 * expirou). O `onFailure` cobre a recusa que dá para detectar — a que lança.
 * A que não lança ninguém detecta daqui, e é por isso que quem chama não deve
 * fechar a tela como se tivesse imprimido.
 *
 * O `html` recebido deve vir pronto e já escapado na origem (é o caso do
 * `buildReceiptHtml`) — este utilitário não sanitiza nada.
 */
export function printHtmlInIframe(html: string, onFailure?: () => void): boolean {
	const iframe = document.createElement("iframe");
	iframe.style.position = "fixed";
	iframe.style.right = "0";
	iframe.style.bottom = "0";
	// 1px, e não 0: há driver de impressão em Android que manda folha em branco
	// quando a viewport do documento é nula. Continua invisível na prática.
	iframe.style.width = "1px";
	iframe.style.height = "1px";
	iframe.style.opacity = "0";
	iframe.style.border = "none";
	// Script bloqueado (sem `allow-scripts`) no documento que de fato vai para a
	// impressora — é o sink que executa, e o recibo é HTML montado à mão.
	// `allow-same-origin` mantém o logotipo do ORI carregando sob a CSP
	// (`img-src 'self'`); `allow-modals` é o que permite o `print()`.
	// NÃO acrescente `allow-scripts`: junto com `allow-same-origin` ele desfaz o
	// isolamento inteiro, e é a combinação clássica de "sandbox que não isola".
	iframe.setAttribute("sandbox", "allow-same-origin allow-modals");

	const remove = () => {
		if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
	};

	// `print()` uma vez só: sem a trava, `onload` e o timer de segurança abrem a
	// caixa de impressão duas vezes.
	let printed = false;
	const triggerPrint = () => {
		if (printed) return;
		printed = true;
		try {
			iframe.contentWindow?.focus();
			iframe.contentWindow?.print();
		} catch {
			// Recusa detectável. Não derruba a tela de quem clicou, mas TAMBÉM não
			// fica calado — era esse silêncio o defeito original.
			remove();
			onFailure?.();
		}
	};

	// `onload` ANTES do `srcdoc`, e `srcdoc` em vez de `document.write`. Com
	// `write` o handler era instalado depois que o load do `about:blank` já
	// tinha passado, então quem imprimia era sempre o timer — e imprimia antes
	// de o logotipo carregar, com o `onload` posterior engolido pela trava.
	// Assim o caminho bom volta a ser o `onload`, que espera as imagens.
	iframe.onload = triggerPrint;
	iframe.srcdoc = html;
	document.body.appendChild(iframe);

	if (!iframe.contentWindow && !iframe.contentDocument) {
		remove();
		return false;
	}

	// Rede de segurança para o navegador em que o `onload` não dispara. Prazo
	// folgado de propósito: 300 ms chegava antes das imagens.
	setTimeout(triggerPrint, 1500);

	// Descarte: no fim da impressão quando o navegador avisa, e um TETO de 60 s
	// para o caso de o aviso não vir. Não é piso — o `onafterprint` remove na
	// hora. Prazo longo porque há navegador cujo `print()` não bloqueia
	// (Safari), e remover o iframe com a caixa de impressão ainda aberta troca
	// lixo no DOM por folha em branco.
	try {
		if (iframe.contentWindow) iframe.contentWindow.onafterprint = remove;
	} catch {
		// Sem acesso ao contentWindow: fica só o teto do timer.
	}
	setTimeout(remove, 60000);

	return true;
}
